<template>
  <div class="container pb-6 pt-10.5">
    <h2
      v-if="data.title"
      class="ui-h5 mb-8 text-center normal-case"
      v-html="data.title"
    />
    <div class="mb-10.5 grid grid-cols-4 gap-5 lg:grid-cols-7">
      <button
        v-for="shape in shapesData"
        :key="shape.type"
        type="button"
        :class="
          selectedShape?.type === shape.type
            ? 'border-red-80'
            : 'border-gray-10'
        "
        class="group flex flex-col items-center gap-3.5 border bg-white px-3 py-2.5 lg:py-5"
        @click="changeShape(shape)"
      >
        <UiIcon
          :name="`stone-shape/${shape.type}`"
          skip-svgo
          :class="
            selectedShape?.type === shape.type
              ? `[&_path[stroke]]:stroke-red-80 [&_path[stroke]]:stroke-[0.5] [&_stop:not([stop-color='white'])]:[stop-color:#FFD9C5]`
              : `group-hover:[&_path[stroke]]:stroke-red-80/50 [&_path[stroke]]:stroke-[#6C779C] [&_path[stroke]]:stroke-[0.25] group-hover:[&_path[stroke]]:stroke-[0.5] [&_stop:not([stop-color='white'])]:[stop-color:#D7E6ED] group-hover:[&_stop:not([stop-color='white'])]:[stop-color:rgba(255,217,197,0.5)]`
          "
          class="size-8 transition-all lg:size-15"
        />
        <span class="ui-text-captions min-h-4.5 uppercase leading-[normal]">
          {{ shape.short_title }}
        </span>
      </button>
    </div>

    <h5 class="mb-6 scroll-mt-[calc(var(--header-height)+1.5rem)]">
      <span class="font-bold">{{ selectedShape.title }}</span>
      {{ $t("millimetersMmToCaratWeight") }}
    </h5>

    <div class="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-12">
      <div>
        <div class="ui-text-m mb-5 uppercase">{{ $t("stoneSizeInMm") }}</div>
        <UiNativeSelect
          v-model="selectedSize"
          :items="selectedShape.data"
          floating-label
          :label="$t('selectSize')"
          title-key="mm"
          value-key="mm"
          name="select-size"
        />
      </div>

      <div>
        <div class="ui-text-m mb-5 uppercase">{{ $t("caratWeight") }}</div>
        <UiInput readonly :model-value="selectedCaratWeightBySize" size="md" />
      </div>
    </div>

    <UiExpandedBlock v-model="isOpen" class="flex flex-col">
      <template #activator="{ attrs }">
        <UiButton variant="text" v-bind="attrs" class="mx-auto">
          <span>
            {{
              $t(
                isOpen
                  ? "hideMmToCaratWeightChart"
                  : "showMmToCaratWeightChart",
              )
            }}
          </span>
          <UiIcon
            name="chevron_up"
            class="ml-2 w-6 transition"
            :class="[isOpen ? '' : 'rotate-180']"
          />
        </UiButton>
      </template>
      <template #content>
        <transition
          enter-active-class="transition duration-1000 ease-out"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="transition duration-1000 ease-out"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            v-if="showTable"
            class="grid grid-cols-1 items-start gap-6 lg:mb-18 lg:grid-cols-2 lg:gap-12"
          >
            <table
              v-for="(table, idx) in tableChunks"
              :key="selectedShape.type + idx"
              class="table-auto [&_td]:w-1/2"
            >
              <thead>
                <tr class="ui-text-l bg-off-white-30 uppercase">
                  <th
                    class="border-gray-30 border py-3 text-center font-normal"
                  >
                    {{ $t("mMSize") }}
                  </th>
                  <th
                    class="border-gray-30 border py-3 text-center font-normal"
                  >
                    {{ $t("caratWeight") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in table" :key="item.mm">
                  <td
                    class="ui-text-m border-gray-30 border px-4 py-3.5 text-center"
                  >
                    {{ item.mm }}
                  </td>
                  <td
                    class="ui-text-m border-gray-30 border px-4 py-3.5 text-center"
                  >
                    {{ item.weight }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </transition>
      </template>
    </UiExpandedBlock>
  </div>
</template>

<script setup lang="ts">
import { chunk } from "lodash-es";
import { RichTextModuleData } from "ecom-types";
import { SizeToCarat } from "~/configs";

withDefaults(
  defineProps<{
    data: RichTextModuleData;
    blockIndex?: number;
  }>(),
  {
    blockIndex: -1,
  },
);

const { t } = useI18n();

const shapesData = [
  {
    title: t("roundCut"),
    type: "round",
    short_title: t("round"),
    data: SizeToCarat.round,
  },
  {
    title: t("princessSquareCut"),
    type: "princess",
    short_title: t("princess"),
    data: SizeToCarat.princess,
  },
  {
    title: t("asscherCut"),
    type: "asscher",
    short_title: t("asscher"),
    data: SizeToCarat.asscher,
  },
  {
    title: t("cushionCut"),
    type: "cushion",
    short_title: t("cushion"),
    data: SizeToCarat.cushion,
  },
  {
    title: t("ovalCut"),
    type: "oval",
    short_title: t("oval"),
    data: SizeToCarat.oval,
  },
  {
    title: t("radiantCut"),
    type: "radiant",
    short_title: t("radiant"),
    data: SizeToCarat.radiant,
  },
  {
    title: t("pearCut"),
    type: "pear",
    short_title: t("pear"),
    data: SizeToCarat.pear,
  },
  {
    title: t("heartCut"),
    type: "heart",
    short_title: t("heart"),
    data: SizeToCarat.heart,
  },
  {
    title: t("marquiseCut"),
    type: "marquise",
    short_title: t("marquise"),
    data: SizeToCarat.marquise,
  },
  {
    title: t("triangleCut"),
    type: "triangle",
    short_title: t("triangle"),
    data: SizeToCarat.triangle,
  },
  {
    title: t("trillionCut"),
    type: "trillion",
    short_title: t("trillion"),
    data: SizeToCarat.trillion,
  },
  {
    title: t("baguetteStraightCut"),
    type: "baguette",
    short_title: t("baguette"),
    data: SizeToCarat.baguette,
  },
  {
    title: t("baguetteTaperedCut"),
    type: "tapered-baguette",
    short_title: t("taperedBaguette"),
    data: SizeToCarat.taperedBaguette,
  },
  {
    title: t("emeraldCut"),
    type: "emerald",
    short_title: t("emerald"),
    data: SizeToCarat.emerald,
  },
];
type ShapeType = (typeof shapesData)[number];

const selectedShape = ref<ShapeType>(shapesData[0]);
const showTable = ref(true);
const isOpen = ref(false);

const selectedSize = ref<string>();

const tableChunks = computed(() => {
  return chunk(
    selectedShape.value.data,
    Math.ceil(selectedShape.value.data.length / 2),
  );
});

const selectedCaratWeightBySize = computed(() => {
  return selectedShape.value.data.find((el) => el.mm === selectedSize.value)
    ?.weight;
});

async function changeShape(shape: ShapeType) {
  if (selectedShape.value.type !== shape.type) {
    selectedSize.value = "";
  }
  showTable.value = false;
  isOpen.value = true;

  await nextTick();
  showTable.value = true;
  selectedShape.value = shape;
}
</script>
